/**
 * Where our confidence in a suggested vendor comes from, as a color:
 * green = they quoted Kono directly or quoted / sold to California Metals;
 * yellow = scraped from their website or verified line card, no quote yet;
 * gray = proposed by AI (or only mentioned in the archive), never quoted.
 * Plus a red chip when the vendor is outside the US.
 */
import type { RfqVendorSuggestion } from "./api";

export const PROVENANCE_LABEL: Record<NonNullable<RfqVendorSuggestion["provenance"]>, string> = {
  quoted: "Quoted us / California Metals",
  website: "From their website",
  ai: "Proposed by AI, never quoted",
};

export const PROVENANCE_CLASS: Record<NonNullable<RfqVendorSuggestion["provenance"]>, string> = {
  quoted: "bg-green-100 text-green-800 border border-green-300",
  website: "bg-yellow-100 text-yellow-800 border border-yellow-300",
  ai: "bg-gray-100 text-gray-600 border border-gray-300",
};

/** Falls back to the source when an older response has no provenance. */
export const provenanceOf = (v: RfqVendorSuggestion): NonNullable<RfqVendorSuggestion["provenance"]> => {
  if (v.provenance) return v.provenance;
  if (v.source === "past-rfq") return "quoted";
  if (v.source === "inventory" || v.source === "line-card") return "website";
  return "ai";
};

export function VendorProvenanceChip({ vendor, className = "" }: { vendor: RfqVendorSuggestion; className?: string }) {
  const p = provenanceOf(vendor);
  return (
    <>
      <span
        className={`rounded-full px-1.5 py-0.5 text-[0.6rem] font-medium ${PROVENANCE_CLASS[p]} ${className}`}
        title={vendor.provenanceLabel || PROVENANCE_LABEL[p]}
      >
        {vendor.provenanceLabel || PROVENANCE_LABEL[p]}
      </span>
      {vendor.abroad && (
        <span
          className={`rounded-full border border-red-300 bg-red-50 px-1.5 py-0.5 text-[0.6rem] font-medium text-red-700 ${className}`}
          title="Outside the US: longer freight and customs. Listed last; ask only when no domestic vendor can supply."
        >
          Abroad{vendor.country ? ` (${vendor.country})` : ""} - last resort
        </span>
      )}
    </>
  );
}
